import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faSparkles } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Button } from '@components/main-components/button/Button';
import { ShareAndEarn } from '@components/main-components/media/illustrations/ShareAndEarn';
import { ShareModalRelease } from '@components/modals/ShareModal';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  DynamicFeePercentInfoFragmentDoc,
  getFragment,
  makeFragmentData,
  ReleaseShareAndEarnFragmentDoc,
} from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { usePrefetchEmbed } from '@hooks/usePrefetchEmbed';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { trackClickEvent } from '@lib/analytics';
import {
  EARN_RELEASE_FEE_COPY,
  SHARE_AND_EARN_COPY,
  useDynamicFeePercent,
} from '../modals/ShareModal/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseShareAndEarn on Release {
    id
    publicAffiliateFeePercent
    samConfig {
      id
      affiliateFeePercent
    }
    ...ReleaseUpdates
    ...ReleaseReferrableInfo
  }
`);

const StyledButton = styled(Button, {
  marginTop: 24,
  maxWidth: 'unset',
  marginX: 'auto',
  width: '100%',
  '@tablet': {
    marginTop: 32,
  },
});

export const ShareAndEarnButton = ({
  releaseFrag,
  isBanner = false,
}: {
  releaseFrag: FragmentType<ReleaseShareAndEarnFragmentDoc>;
  isBanner?: boolean;
}) => {
  const { openModal } = ModalContext.useContainer();
  const release = getFragment(ReleaseShareAndEarnFragmentDoc, releaseFrag);

  const { isShareReferrable } = useCheckReferrableRelease({
    release,
  });

  const { data: releaseInfo } = useSubscribeRelease({
    release,
  });

  const feePercent = useDynamicFeePercent({
    releaseInfo: makeFragmentData(
      {
        isFirstPhaseCompleted: releaseInfo.isFirstPhaseCompleted,
        publicAffiliateFeePercent: release.publicAffiliateFeePercent,
        samConfig: release.samConfig,
      },
      DynamicFeePercentInfoFragmentDoc,
    ),
  });

  usePrefetchEmbed({
    releaseId: release.id,
  });

  if (!isShareReferrable) return null;

  const onButtonClick = () => {
    trackClickEvent({
      event: 'Share Modal Opened',
      properties: {
        associationId: release.id,
        associationType: 'Release',
        sourceComponent: 'Share and Earn Button',
      },
    });
    openModal(ModalType.SHARE, {
      body: <ShareModalRelease releaseId={release.id} />,
    });
  };

  if (isBanner) {
    return (
      <Banner onClick={onButtonClick}>
        <Wrapper>
          <ShareAndEarn />
          <Info>
            <h3>{SHARE_AND_EARN_COPY(feePercent)}</h3>
            <BannerParagraph>{EARN_RELEASE_FEE_COPY}</BannerParagraph>
          </Info>
        </Wrapper>
        <FontAwesomeIcon icon={faChevronRight} />
      </Banner>
    );
  }

  return (
    <StyledButton
      leadingIcon={<FontAwesomeIcon icon={faSparkles} />}
      variant="secondary"
      label={SHARE_AND_EARN_COPY(feePercent)}
      size="L"
      onClick={onButtonClick}
    />
  );
};

const BannerParagraph = styled('p', {
  whiteSpace: 'pre-wrap',
  fontSize: 'small',

  '@tablet': {
    fontSize: 'medium',
  },
});

const Banner = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  backgroundColor: '$base800',
  color: '$base400',
  padding: '16px 24px',
  borderRadius: 8,
  cursor: 'pointer',
  marginTop: 24,
  marginX: 'auto',

  '@tablet': {
    marginTop: 48,
  },

  '&:hover, &:focus, &:active': {
    backgroundColor: '$base900',

    svg: {
      color: '$white',
    },
  },
});

const Wrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',

  gap: 16,
  svg: {
    flexShrink: 0,
  },
});

const Info = styled('div', {
  display: 'flex',
  flexDirection: 'column',

  h3: {
    base: 'L',
    color: '$white',
    fontStyle: 'semibold',
    marginBottom: 4,
  },
});
